import React, { useMemo } from 'react';
import {
  AbsoluteFill,
  Sequence,
  Audio,
  Img,
  Video,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
} from 'remotion';
import { TransitionSeries, linearTiming, type TransitionPresentation } from '@remotion/transitions';
import { fade } from '@remotion/transitions/fade';
import { slide } from '@remotion/transitions/slide';
import { wipe } from '@remotion/transitions/wipe';
import { flip } from '@remotion/transitions/flip';
import { clockWipe } from '@remotion/transitions/clock-wipe';
import type { AIPlayerProps, AIScene, AITextBlock, AITransition } from '../ai-schema';
import { getAIVideoMetrics } from '../ai-schema';
import { getVoiceoverVolume } from '../audio';
import { getStylePreset } from '../brand/presets';
import { FONTS } from '../brand/fonts';
import { FadeIn, SlideUp, TextReveal, easings, secondsToFrames } from '../motion';
import { resolveMediaSrc } from '../shared/media';
import type { Caption, CaptionWord } from '../schemas';

type BasicTransitionDirection = 'from-left' | 'from-right' | 'from-top' | 'from-bottom';

const buildCaptionChunks = (words: CaptionWord[]): Array<{ words: CaptionWord[]; startMs: number; endMs: number }> => {
  const chunks: Array<{ words: CaptionWord[]; startMs: number; endMs: number }> = [];
  let current: CaptionWord[] = [];

  const flush = () => {
    if (!current.length) return;
    chunks.push({
      words: current,
      startMs: current[0]!.startMs,
      endMs: current[current.length - 1]!.endMs,
    });
    current = [];
  };

  for (const word of words) {
    current.push(word);
    if (
      current.length >= 7 ||
      /[.!?,:;]$/.test(word.text) ||
      (current.length >= 4 && word.text.length >= 10)
    ) {
      flush();
    }
  }
  flush();
  return chunks;
};

const pickActiveIndex = (words: CaptionWord[], currentMs: number): number => {
  if (words.length === 0) return -1;
  for (let i = 0; i < words.length; i++) {
    const w = words[i]!;
    if (currentMs >= w.startMs && currentMs <= w.endMs) return i;
    if (currentMs < w.startMs) return Math.max(0, i - 1);
  }
  return words.length - 1;
};

const TimedCaptions: React.FC<{
  captions: Caption;
  currentMs: number;
  style?: AIPlayerProps['spec']['captionStyle'];
}> = ({ captions, currentMs, style }) => {
  const { width, height } = useVideoConfig();
  const isPortrait = height / width >= 1.25;
  const chunks = React.useMemo(() => buildCaptionChunks(captions.words || []), [captions.words]);
  if (!chunks.length) return null;

  const activeChunkIndex = (() => {
    for (let i = 0; i < chunks.length; i++) {
      const c = chunks[i]!;
      if (currentMs >= c.startMs && currentMs <= c.endMs) return i;
      if (currentMs < c.startMs) return Math.max(0, i - 1);
    }
    return chunks.length - 1;
  })();

  const chunk = chunks[activeChunkIndex]!;
  const activeWordIndex = pickActiveIndex(chunk.words, currentMs);
  const position = style?.position || 'bottom';
  const justifyContent =
    position === 'top' ? 'flex-start' : position === 'bottom' ? 'flex-end' : 'center';

  return (
    <AbsoluteFill
      style={{
        justifyContent,
        alignItems: 'center',
        paddingLeft: '7%',
        paddingRight: '7%',
        paddingTop: position === 'top' ? '8%' : undefined,
        paddingBottom: position === 'bottom' ? '16%' : undefined,
        pointerEvents: 'none',
        zIndex: 20,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: isPortrait ? 960 : 980,
          background: style?.backgroundColor || 'rgba(0,0,0,0.82)',
          borderRadius: 28,
          padding: isPortrait ? '24px 28px' : '22px 28px',
          boxShadow: '0 18px 48px rgba(0,0,0,0.55)',
          border: '2px solid rgba(255,255,255,0.14)',
        }}
      >
        <div
          style={{
            fontFamily: style?.fontFamily || FONTS.body,
            fontSize: style?.fontSize || (isPortrait ? 76 : 58),
            fontWeight: 900,
            textAlign: 'center',
            lineHeight: 1.15,
            letterSpacing: -0.8,
            textTransform: 'uppercase',
            textShadow: '0 4px 18px rgba(0,0,0,0.92)',
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '0.22em',
            color: style?.color || '#ffffff',
          }}
        >
          {chunk.words.map((w, i) => {
            const isActive = i === activeWordIndex;
            const isFuture = currentMs < w.startMs;
            const isPast = currentMs > w.endMs;
            const progress = interpolate(currentMs, [w.startMs, w.endMs], [0, 100], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            });
            const color = style?.color || '#ffffff';
            const highlightColor = style?.highlightColor || '#00d1ff';
            const baseOpacity = isFuture ? 0.4 : isPast ? 0.82 : 1;
            const wordStyle: React.CSSProperties = isActive
              ? {
                  background: `linear-gradient(90deg, ${highlightColor} ${progress}%, ${color} ${progress}%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }
              : { color };

            return (
              <span
                key={`${w.startMs}-${i}`}
                style={{
                  display: 'inline-block',
                  opacity: baseOpacity,
                  transform: isActive ? 'scale(1.03)' : 'scale(1)',
                  ...wordStyle,
                }}
              >
                {w.text}
              </span>
            );
          })}
        </div>
      </div>
    </AbsoluteFill>
  );
};

const isGradientValue = (value: string): boolean => /gradient\s*\(/i.test(value.trim());

const resolveTransitionDirection = (
  direction?: AITransition['direction']
): BasicTransitionDirection => {
  switch (direction) {
    case 'right':
      return 'from-right';
    case 'up':
      return 'from-top';
    case 'down':
      return 'from-bottom';
    case 'left':
    default:
      return 'from-left';
  }
};

const asGenericPresentation = <T extends Record<string, unknown>>(
  presentation: TransitionPresentation<T>
): TransitionPresentation<Record<string, unknown>> =>
  presentation as unknown as TransitionPresentation<Record<string, unknown>>;

const getTransitionPresentation = (
  transition: AITransition | undefined,
  dimensions: { width: number; height: number }
): TransitionPresentation<Record<string, unknown>> => {
  const type = transition?.type ?? 'fade';
  switch (type) {
    case 'slide':
      return asGenericPresentation(
        slide({
          direction: resolveTransitionDirection(transition?.direction),
        })
      );
    case 'wipe':
      return asGenericPresentation(
        wipe({
          direction: resolveTransitionDirection(transition?.direction),
        })
      );
    case 'flip':
      return asGenericPresentation(
        flip({
          direction: resolveTransitionDirection(transition?.direction),
        })
      );
    case 'clockWipe':
      return asGenericPresentation(
        clockWipe({
          width: dimensions.width,
          height: dimensions.height,
        })
      );
    case 'fade':
    default:
      return asGenericPresentation(fade());
  }
};

const SceneBackground: React.FC<{ scene: AIScene; fallbackBackground: string }> = ({
  scene,
  fallbackBackground,
}) => {
  const { durationInFrames } = useVideoConfig();
  const rawValue = (scene.background.value || '').trim();
  const value = rawValue === '' || rawValue === 'preset' || rawValue === 'auto'
    ? fallbackBackground
    : rawValue;

  if (scene.background.type === 'image') {
    const frame = useCurrentFrame();
    const { width, height } = useVideoConfig();
    const progress = interpolate(frame, [0, durationInFrames], [0, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });
    const seed = scene.id.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    const sourceWidth = width * 1.8;
    const sourceHeight = height * 1.8;
    const makeViewport = (x: number, y: number, viewportWidth: number, viewportHeight: number) => ({
      x,
      y,
      width: viewportWidth,
      height: viewportHeight,
    });
    const preset = (() => {
      const variants = [
        {
          start: makeViewport(sourceWidth * 0.02, sourceHeight * 0.16, sourceWidth * 0.8, sourceHeight * 0.8),
          end: makeViewport(sourceWidth * 0.18, sourceHeight * 0.08, sourceWidth * 0.62, sourceHeight * 0.62),
        },
        {
          start: makeViewport(sourceWidth * 0.18, sourceHeight * 0.22, sourceWidth * 0.74, sourceHeight * 0.74),
          end: makeViewport(sourceWidth * 0.04, sourceHeight * 0.08, sourceWidth * 0.58, sourceHeight * 0.58),
        },
        {
          start: makeViewport(sourceWidth * 0.08, sourceHeight * 0.02, sourceWidth * 0.82, sourceHeight * 0.82),
          end: makeViewport(sourceWidth * 0.22, sourceHeight * 0.18, sourceWidth * 0.6, sourceHeight * 0.6),
        },
        {
          start: makeViewport(sourceWidth * 0.22, sourceHeight * 0.08, sourceWidth * 0.76, sourceHeight * 0.76),
          end: makeViewport(sourceWidth * 0.1, sourceHeight * 0.2, sourceWidth * 0.56, sourceHeight * 0.56),
        },
      ];
      return variants[seed % variants.length]!;
    })();
    const viewportX = interpolate(progress, [0, 1], [preset.start.x, preset.end.x]);
    const viewportY = interpolate(progress, [0, 1], [preset.start.y, preset.end.y]);
    const viewportWidth = interpolate(progress, [0, 1], [preset.start.width, preset.end.width]);
    const viewportHeight = interpolate(progress, [0, 1], [preset.start.height, preset.end.height]);
    const scaleX = width / viewportWidth;
    const scaleY = height / viewportHeight;
    const scale = Math.max(scaleX, scaleY);
    const translateX = -viewportX * scale;
    const translateY = -viewportY * scale;
    return (
      <AbsoluteFill style={{ overflow: 'hidden', backgroundColor: '#0a0a12' }}>
        <Img
          src={resolveMediaSrc(value)}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: sourceWidth,
            height: sourceHeight,
            objectFit: 'cover',
            transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`,
            transformOrigin: 'top left',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: '#000000',
            opacity: 0.18,
          }}
        />
      </AbsoluteFill>
    );
  }

  if (scene.background.type === 'video') {
    // HeyGen scenes embed the avatar's narration in the video itself; play it at voiceover volume.
    // Standard clip-gen scenes have gibberish audio — mute them and layer separate TTS on top.
    const bgVolume = scene.backgroundAudioEnabled ? getVoiceoverVolume() : 0;
    return (
      <Video
        src={resolveMediaSrc(value)}
        volume={bgVolume}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    );
  }

  return (
    <AbsoluteFill
      style={
        isGradientValue(value)
          ? { backgroundImage: value, backgroundColor: '#000000' }
          : { backgroundColor: value }
      }
    />
  );
};

type TextBlockProps = {
  block: AITextBlock;
  blockIndex: number;
  aspectRatio: '1:1' | '4:5' | '9:16' | '16:9';
  preset: ReturnType<typeof getStylePreset>;
};

const TextBlock: React.FC<TextBlockProps> = ({ block, blockIndex, aspectRatio, preset }) => {
  const { fps, width, height } = useVideoConfig();
  const autoDelay = Math.round(blockIndex * 0.3 * fps);

  const entranceDurationSec = block.entrance?.durationSec ?? 0.55;
  const entranceFrames = Math.max(1, secondsToFrames(entranceDurationSec, fps));

  const resolvedAnimation =
    block.animation ??
    (block.entrance?.type === 'slide'
      ? 'fadeIn'
      : block.entrance
        ? 'fadeIn'
        : 'none');

  const fadeDirection = (() => {
    const d = block.entrance?.direction;
    if (d === 'left' || d === 'right' || d === 'up' || d === 'down') return d;
    return 'up';
  })();

  const style = block.style || {};
  const position = style.position ?? 'center';
  const textAlign = style.textAlign ?? 'center';
  const alignItems =
    textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center';
  const justifyContent =
    position === 'top' ? 'flex-start' : position === 'bottom' ? 'flex-end' : 'center';

  const safePadding = Math.round(
    height * (aspectRatio === '9:16' ? 0.075 : aspectRatio === '4:5' ? 0.065 : 0.06)
  );

  const baseHeadingSize = Math.round(
    height * (aspectRatio === '9:16' ? 0.055 : aspectRatio === '4:5' ? 0.06 : 0.065)
  );

  const text = (block.text || '').trim();
  const isLong = text.length > 56 || text.split(/\s+/).length > 11;

  const fontFamily = style.fontFamily ?? (isLong ? preset.text.fontFamilyBody : preset.text.fontFamilyHeading);
  const fontWeight = style.fontWeight ?? (isLong ? '700' : '800');
  const fontSize = style.fontSize ?? Math.round(isLong ? baseHeadingSize * 0.78 : baseHeadingSize);

  const maxWidth =
    style.maxWidth ??
    Math.round(width * (aspectRatio === '9:16' ? 0.86 : aspectRatio === '4:5' ? 0.88 : 0.9));

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent,
    alignItems,
    padding: style.padding ?? safePadding,
    textAlign,
  };

  const content = (
    <div
      style={{
        fontFamily,
        fontSize,
        fontWeight,
        color: style.color ?? preset.text.color,
        lineHeight: style.lineHeight ?? (isLong ? 1.12 : 1.05),
        letterSpacing: style.letterSpacing ?? (isLong ? 0 : -0.5),
        maxWidth,
        whiteSpace: 'pre-line',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        textShadow:
          preset.id === 'meta_explainer_cream'
            ? '0 6px 30px rgba(0,0,0,0.12)'
            : '0 10px 40px rgba(0,0,0,0.65)',
        backgroundColor: style.backgroundColor,
        padding: style.backgroundColor ? '16px 24px' : undefined,
        borderRadius: style.backgroundColor ? preset.card.borderRadius : undefined,
      }}
    >
      {block.text}
    </div>
  );

  const animatedContent = (() => {
    switch (resolvedAnimation) {
      case 'textReveal':
        return (
          <TextReveal
            durationInFrames={entranceFrames}
            delay={autoDelay}
            direction="left"
            easing={easings.inOut}
          >
            {content}
          </TextReveal>
        );

      case 'slideUp':
        return (
          <SlideUp
            durationInFrames={entranceFrames}
            delay={autoDelay}
            distance={Math.round(height * 0.05)}
            easing={easings.smooth}
          >
            {content}
          </SlideUp>
        );

      case 'fadeIn':
        return (
          <FadeIn
            durationInFrames={entranceFrames}
            delay={autoDelay}
            direction={fadeDirection}
            distance={Math.round(height * 0.02)}
            easing={easings.smooth}
          >
            {content}
          </FadeIn>
        );

      case 'none':
      default:
        return content;
    }
  })();

  return <AbsoluteFill style={containerStyle}>{animatedContent}</AbsoluteFill>;
};

const Scene: React.FC<{
  scene: AIScene;
  aspectRatio: '1:1' | '4:5' | '9:16' | '16:9';
  preset: ReturnType<typeof getStylePreset>;
  showTextOverlays: boolean;
}> = ({ scene, aspectRatio, preset, showTextOverlays }) => {
  const { fps } = useVideoConfig();
  const overlay =
    preset.id === 'meta_explainer_cream'
      ? null
      : (
          <AbsoluteFill
            style={{
              backgroundImage:
                'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.08) 40%, rgba(0,0,0,0.55) 100%)',
            }}
          />
        );

  return (
    <AbsoluteFill>
      <SceneBackground scene={scene} fallbackBackground={preset.background.value} />
      {overlay}
      {showTextOverlays ? (() => {
        const blocks = Array.isArray(scene.textBlocks) ? scene.textBlocks : [];
        if (blocks.length === 0) return null;

        // If all blocks share the same position, sequence them instead of stacking them all at once.
        // This makes AI-generated "step 1/2/3" overlays readable without schema changes.
        const positions = new Set(blocks.map((b) => b.style?.position ?? 'center'));
        const textAligns = new Set(
          blocks.map((b) => String(b.style?.textAlign ?? 'center').toLowerCase())
        );
        // Only sequence truly "stacked" center overlays.
        // Side-by-side comparisons commonly use left/right alignment with the same vertical position.
        const shouldSequence =
          blocks.length > 1 && positions.size <= 1 && textAligns.size <= 1 && textAligns.has('center');

        if (!shouldSequence) {
          return blocks.map((block, index) => (
            <TextBlock
              key={`${scene.id}-text-${index}`}
              block={block}
              blockIndex={index}
              aspectRatio={aspectRatio}
              preset={preset}
            />
          ));
        }

        const totalFrames = Math.max(1, secondsToFrames(scene.durationSec, fps));
        const slot = Math.max(1, Math.floor(totalFrames / blocks.length));
        let cursor = 0;

        return blocks.map((block, index) => {
          const from = cursor;
          const remaining = Math.max(1, totalFrames - from);
          const durationInFrames = index === blocks.length - 1 ? remaining : slot;
          cursor += slot;

          return (
            <Sequence
              key={`${scene.id}-text-${index}`}
              from={from}
              durationInFrames={durationInFrames}
            >
              <TextBlock
                block={block}
                // Inside a Sequence, frame starts at 0; avoid extra per-index entrance delay.
                blockIndex={0}
                aspectRatio={aspectRatio}
                preset={preset}
              />
            </Sequence>
          );
        });
      })() : null}
    </AbsoluteFill>
  );
};

export const AIPlayer: React.FC<AIPlayerProps> = ({ spec }) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();
  const currentMs = (frame / fps) * 1000;
  const scenes = spec.scenes;
  const totalMetrics = getAIVideoMetrics(spec);
  const preset = getStylePreset(spec.stylePreset);
  const narrationEnabled = spec.narrationEnabled !== false;
  const musicEnabled = spec.musicEnabled !== false;
  const showTextOverlays = spec.overlayTextEnabled !== false;

  const voiceoverRanges = useMemo(() => {
    let cursor = 0;
    const ranges: Array<{ start: number; end: number }> = [];
    for (let i = 0; i < scenes.length; i++) {
      const scene = scenes[i];
      const sceneFrames = Math.max(1, secondsToFrames(scene.durationSec, fps));
      // HeyGen scenes have narration baked into the video — they still need music ducking,
      // but via the backgroundAudioEnabled flag rather than voiceover tracks.
      const hasSceneVoiceover =
        narrationEnabled &&
        (scene.backgroundAudioEnabled ||
          Boolean(scene.voiceoverUrl) ||
          (Array.isArray(scene.voiceoverClips) && scene.voiceoverClips.length > 0));
      if (hasSceneVoiceover) {
        ranges.push({ start: cursor, end: cursor + sceneFrames });
      }
      cursor += sceneFrames;
    }
    return ranges;
  }, [scenes, fps, narrationEnabled]);

  const musicVolume = (() => {
    const requestedBase =
      typeof spec.musicVolume === 'number' ? spec.musicVolume : preset.music.baseVolume;
    const base = voiceoverRanges.length > 0 ? Math.max(requestedBase, 0.38) : requestedBase;
    if (!musicEnabled || !spec.musicUrl) return base;
    if (voiceoverRanges.length === 0) return base;

    const ducked = base * Math.max(preset.music.duckedMultiplier, 0.55);
    const fadeFrames = Math.max(1, secondsToFrames(preset.music.fadeSec, fps));

    let multiplier = 1;
    for (const r of voiceoverRanges) {
      if (frame < r.start - fadeFrames || frame > r.end + fadeFrames) continue;

      if (frame < r.start) {
        const t = interpolate(frame, [r.start - fadeFrames, r.start], [1, preset.music.duckedMultiplier], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        multiplier = Math.min(multiplier, t);
        continue;
      }

      if (frame <= r.end) {
        multiplier = Math.min(multiplier, preset.music.duckedMultiplier);
        continue;
      }

      const t = interpolate(frame, [r.end, r.end + fadeFrames], [preset.music.duckedMultiplier, 1], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
      });
      multiplier = Math.min(multiplier, t);
    }

    const mixed = base * multiplier;
    return Math.max(0, Math.min(base, Math.max(ducked, mixed)));
  })();

  return (
    <AbsoluteFill
      style={
        preset.background.type === 'gradient'
          ? { backgroundImage: preset.background.value, backgroundColor: '#000000' }
          : { backgroundColor: preset.background.value }
      }
    >
      {musicEnabled && spec.musicUrl && (
        <Audio src={resolveMediaSrc(spec.musicUrl)} volume={() => musicVolume} />
      )}

      <TransitionSeries>
        {scenes.map((scene, index) => {
          const sceneFrames = Math.max(1, secondsToFrames(scene.durationSec, fps));
          const transition = scene.transition;
          // Only apply a TransitionSeries transition if explicitly provided in the spec.
          // Default should be a hard cut; otherwise narration and scenes feel like they "merge".
          const transitionFrames =
            transition && index < scenes.length - 1
              ? Math.max(
                  1,
                  secondsToFrames(typeof transition.durationSec === 'number' ? transition.durationSec : 0.4, fps)
                )
              : 0;
          // If we overlap two sequences for a transition, pad the first one so the overall duration
          // still matches the sum of scene.durationSec (as used by getAIVideoMetrics()).
          const sequenceFrames =
            index < scenes.length - 1 && transitionFrames > 0 ? sceneFrames + transitionFrames : sceneFrames;
          const voiceoverClips = Array.isArray(scene.voiceoverClips) ? scene.voiceoverClips : [];

          return (
            <React.Fragment key={scene.id}>
              <TransitionSeries.Sequence durationInFrames={sequenceFrames}>
                <Scene
                  scene={scene}
                  aspectRatio={spec.aspectRatio}
                  preset={preset}
                  showTextOverlays={showTextOverlays}
                />
                {/* Skip separate voiceover for HeyGen scenes — narration is baked into the video */}
                {scene.backgroundAudioEnabled ? null :
                narrationEnabled && voiceoverClips.length > 0 ? (
                  <Sequence durationInFrames={sceneFrames}>
                    {voiceoverClips
                      .slice()
                      .sort((a, b) => (a.startSec ?? 0) - (b.startSec ?? 0))
                      .map((clip) => {
                        const from = Math.max(0, Math.round((clip.startSec ?? 0) * fps));
                        const remaining = sceneFrames - from;
                        if (remaining <= 0) return null;
                        return (
                          <Sequence key={clip.id} from={from} durationInFrames={remaining}>
                            <Audio src={resolveMediaSrc(clip.url)} volume={() => getVoiceoverVolume()} />
                          </Sequence>
                        );
                      })}
                  </Sequence>
                ) : narrationEnabled && scene.voiceoverUrl ? (
                  <Sequence durationInFrames={sceneFrames}>
                    <Audio src={resolveMediaSrc(scene.voiceoverUrl)} volume={() => getVoiceoverVolume()} />
                  </Sequence>
                ) : null}
              </TransitionSeries.Sequence>
              {index < scenes.length - 1 && transitionFrames > 0 && (
                <TransitionSeries.Transition
                  presentation={getTransitionPresentation(transition, { width, height })}
                  timing={linearTiming({ durationInFrames: transitionFrames })}
                />
              )}
            </React.Fragment>
          );
        })}
      </TransitionSeries>

      {process.env.NODE_ENV !== 'production' && (
        <AbsoluteFill
          style={{
            position: 'absolute',
            bottom: 24,
            right: 24,
            fontSize: 16,
            color: 'rgba(255,255,255,0.5)',
            fontFamily: FONTS.mono,
          }}
        >
          {Math.round(totalMetrics.durationInFrames / fps)}s
        </AbsoluteFill>
      )}
      {spec.captions?.words?.length ? (
        <TimedCaptions captions={spec.captions} currentMs={currentMs} style={spec.captionStyle} />
      ) : null}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
